import getConfig from "../config";
import { Auth } from "aws-amplify";
import { HttpLink } from "apollo-link-http";
import { onError } from "apollo-link-error";
import { ApolloLink, Observable } from "apollo-link";
import { ApolloClient, InMemoryCache } from "@apollo/client";

const cfg = getConfig();

// Raised when an operation is attempted without a Cognito session, e.g. a
// component still mounted while the user signs out. Amplify rejects with a
// bare string rather than an Error, which Apollo then reports as a network
// error with an `undefined` message; this preserves the reason.
class NotAuthenticatedError extends Error {
  constructor(message) {
    super(message);
    this.name = "NotAuthenticatedError";
  }
}

const request = async (op) => {
  let cs;
  try {
    cs = await Auth.currentSession();
  } catch (e) {
    throw e instanceof Error ? e : new NotAuthenticatedError(String(e));
  }
  op.setContext({
    headers: { Authorization: "Bearer " + cs.idToken.jwtToken },
  });
};

const requestLink = new ApolloLink(
  (operation, forward) =>
    new Observable((observer) => {
      let handle;
      Promise.resolve(operation)
        .then((oper) => request(oper))
        .then(() => {
          handle = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });
        })
        .catch(observer.error.bind(observer));

      return () => {
        if (handle) handle.unsubscribe();
      };
    })
);

// Nothing is served from the cache: the clients outlive a sign out, so a cached
// result could be shown to the next user. Hooks (useQuery) go through
// watchQuery and direct calls through query, so both are set.
const defaultOptions = {
  watchQuery: {
    fetchPolicy: "no-cache",
  },
  query: {
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  },
};

const newClient = (url) => {
  return new ApolloClient({
    link: ApolloLink.from([
      onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors)
          graphQLErrors.map(({ message, locations, path }) =>
            console.log(
              `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
            )
          );
        // being signed out is an expected state, not a fault worth reporting
        if (networkError && !(networkError instanceof NotAuthenticatedError)) {
          console.log(`[Network error]: ${networkError}`);
        }
      }),
      requestLink,
      new HttpLink({ uri: url }),
    ]),
    cache: new InMemoryCache(),
    defaultOptions: defaultOptions,
  });
};

const UserClient = newClient(cfg.backend.graphqlEndPoint);
const ConnXClient = newClient(cfg.connx.graphqlEndPoint);
const ConnXEventClient = newClient(cfg.connxevents.graphqlEndPoint);

// Drops everything the clients hold, including queries still in flight. Called
// whenever the signed in user changes.
const clearClientStores = () =>
  Promise.all(
    [UserClient, ConnXClient, ConnXEventClient].map((c) => c.clearStore())
  ).catch((e) => console.log(`[Apollo clear store error]: ${e}`));

export {
  UserClient,
  ConnXClient,
  ConnXEventClient,
  NotAuthenticatedError,
  defaultOptions,
  clearClientStores,
};
